import { useReapproCategories } from '@gozoki/api'
import {
  useReapproCategoryGroupForm,
  type ReapproCategoryGroupForm,
} from '@gozoki/api/src/forms/useReapproCategoryGroupForm'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert, XIcon } from 'lucide-react'
import { Controller } from 'react-hook-form'
import { toast } from 'sonner'
import { Field } from '~/components/new/app-ui/form-fields/field'
import { Button } from '~/components/ui/button'
import { Checkbox } from '~/components/ui/checkbox'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { TimePicker } from '~/components/ui/time-picker'
import { api } from '~/lib/tuyau'

export interface ReapproCategoryGroupListItem {
  id: number
  name: string
  maxProduction: number | null
  reapproCategories: {
    id: number
    name: string
    computeHour: string
    sendHour: string
    daysBefore: number
  }[]
}

interface ReapproCategoryGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  group?: ReapproCategoryGroupListItem
  onSuccess: () => void
}

const formatHour = (value: string) => value.split(':').slice(0, 2).join(':')

const toFormValues = (
  group?: ReapproCategoryGroupListItem,
): ReapproCategoryGroupForm | undefined => {
  if (!group) return undefined
  const firstCategory = group.reapproCategories[0]
  return {
    name: group.name,
    maxProduction: group.maxProduction,
    computeHour: firstCategory ? formatHour(firstCategory.computeHour) : '06:00',
    sendHour: firstCategory ? formatHour(firstCategory.sendHour) : '07:00',
    daysBefore: firstCategory?.daysBefore ?? 1,
    reapproCategoryIds: group.reapproCategories.map((c) => c.id),
  }
}

export const ReapproCategoryGroupDialog = ({
  open,
  onOpenChange,
  group,
  onSuccess,
}: ReapproCategoryGroupDialogProps) => {
  const queryClient = useQueryClient()
  const { data: categories = [] } = useReapproCategories()
  const form = useReapproCategoryGroupForm(toFormValues(group))
  const { control, handleSubmit, reset } = form

  const invalidateAndClose = () => {
    void queryClient.invalidateQueries({
      queryKey: api.reapproCategoryGroups.index.queryOptions().queryKey,
    })
    onSuccess()
    reset()
    onOpenChange(false)
  }

  const createMut = useMutation(
    api.reapproCategoryGroups.store.mutationOptions({ onSuccess: invalidateAndClose }),
  )
  const updateMut = useMutation(
    api.reapproCategoryGroups.update.mutationOptions({ onSuccess: invalidateAndClose }),
  )
  const isPending = createMut.isPending || updateMut.isPending

  const onSubmit = (data: ReapproCategoryGroupForm) => {
    if (group) {
      updateMut.mutate(
        { params: { id: group.id }, body: data },
        { onError: () => toast.error('Erreur lors de la modification du groupe') },
      )
    } else {
      createMut.mutate(
        { body: data },
        { onError: () => toast.error('Erreur lors de la création du groupe') },
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-150 max-w-[calc(100%-2rem)] flex flex-col p-0 gap-0"
        showCloseButton={false}
      >
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-border">
          <DialogTitle className="text-lg font-medium">
            {group ? 'Modifier le groupe' : 'Créer un groupe'}
          </DialogTitle>
          <DialogClose render={<Button variant="ghost" size="icon-sm" />}>
            <XIcon />
            <span className="sr-only">Fermer</span>
          </DialogClose>
        </div>

        <form
          id="reappro-category-group-form"
          onSubmit={handleSubmit(onSubmit, () => toast.error('Vérifiez les champs du formulaire'))}
          className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col gap-4"
        >
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <Field label="Nom du groupe" error={fieldState.error?.message}>
                <Input {...field} />
              </Field>
            )}
          />

          <Controller
            name="maxProduction"
            control={control}
            render={({ field, fieldState }) => (
              <Field
                label="Max de production (vide = sans limite)"
                error={fieldState.error?.message}
              >
                <Input
                  type="number"
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? null : e.target.valueAsNumber)
                  }
                />
              </Field>
            )}
          />

          <div className="grid grid-cols-3 gap-4">
            <Controller
              name="computeHour"
              control={control}
              render={({ field, fieldState }) => (
                <Field label="H. calcul" error={fieldState.error?.message}>
                  <TimePicker {...field} />
                </Field>
              )}
            />
            <Controller
              name="sendHour"
              control={control}
              render={({ field, fieldState }) => (
                <Field label="H. envoi" error={fieldState.error?.message}>
                  <TimePicker {...field} />
                </Field>
              )}
            />
            <Controller
              name="daysBefore"
              control={control}
              render={({ field, fieldState }) => (
                <Field label="J-X" error={fieldState.error?.message}>
                  <Input {...field} />
                </Field>
              )}
            />
          </div>

          <Controller
            name="reapproCategoryIds"
            control={control}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Catégories du groupe</span>
                <div className="border border-border rounded-lg divide-y divide-border/50 max-h-64 overflow-y-auto">
                  {categories.map((category) => {
                    const takenByOtherGroup =
                      category.reapproCategoryGroupId != null &&
                      category.reapproCategoryGroupId !== group?.id
                    const checked = field.value.includes(category.id)
                    return (
                      <label
                        key={category.id}
                        className={`flex items-center gap-2 px-3 py-2 text-sm ${takenByOtherGroup ? 'opacity-50' : 'cursor-pointer hover:bg-muted/30'}`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={takenByOtherGroup}
                          onCheckedChange={() =>
                            field.onChange(
                              checked
                                ? field.value.filter((id) => id !== category.id)
                                : [...field.value, category.id],
                            )
                          }
                        />
                        <span>{category.name}</span>
                        {takenByOtherGroup && (
                          <span className="text-xs text-muted-foreground">
                            (déjà dans un autre groupe)
                          </span>
                        )}
                      </label>
                    )
                  })}
                </div>
                {fieldState.error && (
                  <span className="text-xs text-destructive">{fieldState.error.message}</span>
                )}
              </div>
            )}
          />

          <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
            <TriangleAlert size={16} className="mt-0.5 shrink-0" />
            <span>
              Enregistrer écrasera l'heure de calcul, l'heure d'envoi et le J-X de toutes les
              catégories sélectionnées.
            </span>
          </div>
        </form>

        <DialogFooter className="shrink-0 px-6 py-4 border-t border-border">
          <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
          <Button type="submit" form="reappro-category-group-form" disabled={isPending}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
